// src/errors/handlers/global-error.handler.ts

/**
 * Global Error Handler
 *
 * WHY: Single point of control for all error handling
 * WHAT: Orchestrates logging, reporting, and notifications
 * BENEFIT:
 *   - Consistent error handling across the app
 *   - Centralized configuration
 *   - Pluggable handlers for different error types
 *
 * DESIGN DECISIONS:
 * 1. Singleton pattern for global access
 * 2. Configurable through options
 * 3. Supports auth error callbacks
 * 4. Throttles duplicate errors
 */

import {
  AppError,
  AuthenticationError,
  CancellationError,
  ConflictError,
  isAppError,
  isCancellationError,
} from "../classes";
import { ErrorCategory, type ErrorHandlerOptions } from "../types";
import { errorConfig } from "@/config/error.config";
import { errorLogger } from "../utils/error-logger";
import { errorThrottler } from "../utils/error-throttler";

import { errorReporter } from "../monitoring/error-reporter";

import { toastService } from "@/core/services/toast.service";

/**
 * Callback for authentication errors
 */
type AuthErrorCallback = (error: AuthenticationError) => void;

/**
 * Global Error Handler Class
 *
 * Singleton that handles all application errors.
 */
class GlobalErrorHandler {
  private static instance: GlobalErrorHandler;

  /**
   * Callback for authentication errors
   * Set by the auth provider to handle logout/refresh
   */
  private authErrorCallback?: AuthErrorCallback;

  private constructor() {
    // Private constructor for singleton
  }

  /**
   * Get singleton instance
   */
  static getInstance(): GlobalErrorHandler {
    if (!GlobalErrorHandler.instance) {
      GlobalErrorHandler.instance = new GlobalErrorHandler();
    }
    return GlobalErrorHandler.instance;
  }

  /**
   * Set callback for authentication errors
   * Called by auth provider on mount
   */
  setAuthErrorCallback(callback: AuthErrorCallback): void {
    this.authErrorCallback = callback;
  }

  /**
   * Clear auth error callback
   * Called by auth provider on unmount
   */
  clearAuthErrorCallback(): void {
    this.authErrorCallback = undefined;
  }

  /**
   * Handle any error
   *
   * @param error - Any error type
   * @param options - Handler options
   * @returns Normalized AppError
   */
  handle(error: unknown, options: ErrorHandlerOptions = {}): AppError {
    const {
      showToast = true,
      logError = true,
      reportError = true,
      rethrow = false,
      fallbackMessage,
      context,
    } = options;

    // Normalize to AppError
    const appError = this.normalizeError(error, fallbackMessage, context);

    // Handle cancellation errors silently
    if (
      isCancellationError(appError) &&
      (appError as CancellationError).shouldIgnore()
    ) {
      return appError;
    }

    // Check throttling
    const shouldThrottle = errorThrottler.shouldThrottle({
      code: appError.code,
      message: appError.message,
      statusCode: appError.statusCode,
    });

    // Log error (unless throttled)
    if (logError && !shouldThrottle) {
      this.logError(appError);
    }

    // Report to monitoring (unless throttled or shouldn't report)
    if (reportError && !shouldThrottle && this.shouldReport(appError)) {
      this.reportError(appError);
    }

    // Show toast notification (unless throttled)
    if (showToast && !shouldThrottle) {
      this.showToast(appError);
    }

    // Handle auth errors
    if (appError instanceof AuthenticationError) {
      this.handleAuthError(appError);
    }

    // Rethrow if requested
    if (rethrow) {
      throw appError;
    }

    return appError;
  }

  /**
   * Normalize any error to AppError
   */
  private normalizeError(
    error: unknown,
    fallbackMessage?: string,
    context?: Partial<ErrorHandlerOptions["context"]>,
  ): AppError {
    if (isAppError(error)) {
      return context ? error.withContext(context) : error;
    }

    if (error instanceof Error) {
      return new AppError(fallbackMessage ?? error.message, {
        cause: error,
        context: context ? { ...context, timestamp: new Date() } : undefined,
      });
    }

    const message =
      typeof error === "string"
        ? error
        : (fallbackMessage ?? "An error occurred");

    return new AppError(message, {
      cause: error,
      context: context ? { ...context, timestamp: new Date() } : undefined,
    });
  }

  /**
   * Log error based on severity
   */
  private logError(error: AppError): void {
    if (!errorConfig.logging.enabled) return;
    errorLogger.logError(error.serialize());
  }

  /**
   * Check if error should be reported to monitoring
   */
  private shouldReport(error: AppError): boolean {
    // Don't report if reporting is disabled
    if (!errorConfig.reporting.enabled) return false;

    // Don't report cancelled requests
    if (error.category === ErrorCategory.CANCELLED) return false;

    // Don't report validation errors (user input issues)
    if (error.category === ErrorCategory.VALIDATION) return false;

    // Don't report expected auth errors
    if (error.category === ErrorCategory.AUTHENTICATION) return false;

    // Report all other errors
    return true;
  }

  /**
   * Report error to monitoring service
   */
  private reportError(error: AppError): void {
    errorReporter.captureError(error);
  }

  /**
   * Show toast notification
   */
  private showToast(error: AppError): void {
    if (!errorConfig.toast.enabled) return;
    const message = error.getUserMessage();

    switch (error.category) {
      case ErrorCategory.NETWORK:
        toastService.error(ErrorCategory.NETWORK, message);
        break;
      case ErrorCategory.AUTHENTICATION:
        toastService.error(ErrorCategory.AUTHENTICATION, message);
        break;
      case ErrorCategory.AUTHORIZATION:
        toastService.error(ErrorCategory.AUTHORIZATION, message);
        break;
      case ErrorCategory.VALIDATION:
        // ✅ ConflictError falls here (category = VALIDATION)
        // But we can show a more specific toast for conflicts
        if (error instanceof ConflictError) {
          toastService.warning(
            ErrorCategory.VALIDATION,
            error.getResolutionHint(),
          );
        } else {
          toastService.error(ErrorCategory.VALIDATION, message);
        }
        break;
      case ErrorCategory.SERVER:
        toastService.error(ErrorCategory.SERVER, message);
        break;
      case ErrorCategory.RATE_LIMIT:
        toastService.error(ErrorCategory.RATE_LIMIT, message);
        break;
      default:
        toastService.error(ErrorCategory.UNKNOWN, message);
    }
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: AuthenticationError): void {
    if (this.authErrorCallback) {
      this.authErrorCallback(error);
    }
  }

  /**
   * Create a scoped handler for a specific component/feature
   */
  createScopedHandler(
    scope: string,
  ): (error: unknown, options?: ErrorHandlerOptions) => AppError {
    return (error, options = {}) => {
      const contextWithScope = {
        ...options.context,
        component: scope,
      };

      return this.handle(error, {
        ...options,
        context: contextWithScope,
      });
    };
  }
}

/**
 * Export singleton instance
 */
export const globalErrorHandler = GlobalErrorHandler.getInstance();
